import { BehaviorSubject } from 'rxjs';
import { ClientSideSortingService, SortConfig } from './client-side-sorting.service';
import { TableViewHeader } from '@services/ui-services/models';

export interface AutoTableSortingMixin<T> {
  setupAutoTableSorting(
    dataSubject: BehaviorSubject<T[] | undefined>,
    originalDataSubject: BehaviorSubject<T[] | undefined>,
    tableHeaders: TableViewHeader[],
    customSortValues?: Record<string, (item: T) => any>
  ): (sortConfig: SortConfig) => void;
}

/**
 * Core sorting logic shared across all implementations
 */
function applySortingToSubjects<T>(
  clientSideSortingService: ClientSideSortingService,
  dataSubject: BehaviorSubject<T[] | undefined>,
  originalDataSubject: BehaviorSubject<T[] | undefined>,
  sortConfig: SortConfig,
  tableHeaders: TableViewHeader[],
  customSortValues?: Record<string, (item: T) => any>
): void {
  const originalData = originalDataSubject.getValue();
  if (originalData) {
    const sortedData = clientSideSortingService.sortDataAuto(originalData, sortConfig, {
      tableHeaders,
      customSortValues: customSortValues || {},
    });
    dataSubject.next(sortedData);
  }
}

/**
 * Mixin function to add auto table sorting capabilities to any store
 *
 * Usage in your store:
 * 1. Inject ClientSideSortingService
 * 2. Call setupAutoTableSorting in your data loading method
 * 3. Use the returned handler for sorting
 */
export function createAutoTableSortingMixin<T>(
  clientSideSortingService: ClientSideSortingService
): AutoTableSortingMixin<T> {
  return {
    setupAutoTableSorting(
      dataSubject: BehaviorSubject<T[] | undefined>,
      originalDataSubject: BehaviorSubject<T[] | undefined>,
      tableHeaders: TableViewHeader[],
      customSortValues?: Record<string, (item: T) => any>
    ): (sortConfig: SortConfig) => void {
      return (sortConfig: SortConfig) => {
        applySortingToSubjects(
          clientSideSortingService,
          dataSubject,
          originalDataSubject,
          sortConfig,
          tableHeaders,
          customSortValues
        );
      };
    },
  };
}

/**
 * Simplified utility for quick setup
 */
export class AutoTableSortingHelper {
  constructor(private clientSideSortingService: ClientSideSortingService) {}

  /**
   * One-liner setup for table sorting
   */
  setup<T>(
    dataSubject: BehaviorSubject<T[] | undefined>,
    originalDataSubject: BehaviorSubject<T[] | undefined>,
    tableHeaders: TableViewHeader[],
    customSortValues?: Record<string, (item: T) => any>
  ): (sortConfig: SortConfig) => void {
    return (sortConfig: SortConfig) => {
      applySortingToSubjects(
        this.clientSideSortingService,
        dataSubject,
        originalDataSubject,
        sortConfig,
        tableHeaders,
        customSortValues
      );
    };
  }

  /**
   * Static method for even simpler usage
   */
  static createSortHandler<T>(
    clientSideSortingService: ClientSideSortingService,
    dataSubject: BehaviorSubject<T[] | undefined>,
    originalDataSubject: BehaviorSubject<T[] | undefined>,
    tableHeaders: TableViewHeader[],
    customSortValues?: Record<string, (item: T) => any>
  ): (sortConfig: SortConfig) => void {
    return (sortConfig: SortConfig) => {
      applySortingToSubjects(
        clientSideSortingService,
        dataSubject,
        originalDataSubject,
        sortConfig,
        tableHeaders,
        customSortValues
      );
    };
  }
}
